/* eslint-disable */
import { useState, useMemo, useEffect } from 'react';
import { generatePath, useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import qs from 'qs';
import { AppRouteContext } from 'contexts';
import { QUERY_DELAY } from 'components/Shared/constants';
import { useDispatch, useSelector } from 'react-redux';
import { checkPermission } from '../helpers/checkPermissions';
import { ALL_PRIVATE_ROUTES, ALL_ROUTES } from '../config/routes';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../redux_store/reducer/reducers/notificationsReducer';
import { tryToParseRouteFromString } from './UserProvider';

const getParsedBc = (search, pathname) => {
  const parsedSearch =
    search &&
    qs.parse(search, {
      ignoreQueryPrefix: true,
    });

  if (pathname) {
    const parsedRoute = tryToParseRouteFromString(pathname);
    return parsedRoute ? [parsedRoute] : [];
  }

  const bcData = parsedSearch && parsedSearch.bc
    ? JSON.parse(atob(parsedSearch.bc))
    : [];
  
  // Filter out any undefined/null items
  return bcData.filter(item => item && item.path);
};

const AppRouteProvider = ({ children }) => {
  /*
    The idea is to store breadcrumbs params in URL query string.
    It helps to restore breadcrumbs after reloading the page.
    The query string is encoded by Base64.
    App route schema:
    {
      path: String,
      params: Object,
      title: String,
      removeQueryString: Boolean,
      clearBreadcrumbs: Boolean,
    }
  */
  const navigate = useNavigate();
  const { search } = useLocation();
  const [bc, setBc] = useState(getParsedBc(search));
  const { metadata } = useSelector((state) => state.user);
  const { disabledFeatures } = useSelector((state) => state.appSettings);
  const dispatch = useDispatch();
  const { en: { notifications } } = useSelector((state) => state.i18n);

  const getBc = (newItem) => {
    // clear an array and set only first item
    // case: sidebar items click, not-found route
    if (newItem.clearBreadcrumbs || !newItem.path) {
      return [newItem];
    }
    // filter out any undefined/null items from breadcrumbs
    const validBc = bc.filter(item => item && item.path);
    // find the same route in breadcrumbs
    // case: click on breadcrumb in navigation
    const foundItem = validBc.find((item) => item.path === newItem.path);
    const index = foundItem ? validBc.indexOf(foundItem) : -1;
    return index === -1 ? [...validBc, newItem] : [...validBc].slice(0, index + 1);
  };

  const eventListener = () => {
    /*
      handle changing route outside the dashboard
      case: browser back button click
    */
    const newBc = getParsedBc(document.location.search, document.location.pathname);
    setBc(newBc);
  };

  const updateBc = (newAppRoute) => {
    const newBc = getBc(newAppRoute);
    setBc(newBc);
    return newBc;
  };

  useEffect(() => {
    window.addEventListener('popstate', eventListener, true);
    return () => {
      window.removeEventListener('popstate', eventListener);
    };
  }, []);

  const updateRoute = async (newAppRoute, options = {}, _metadata) => {
    const newBc = updateBc(newAppRoute);
    const parsedRoute = ALL_PRIVATE_ROUTES.find(i => i.path === newAppRoute.path);
    const _isAllowed = checkPermission(
        (_metadata || metadata)?.currentAccountPermissions,
        parsedRoute?.additional?.permission)
      &&
      !disabledFeatures.includes(parsedRoute?.additional?.disabledId);
    if (!_isAllowed) {
      dispatch(
        enqueueSnackbar({
          message: notifications.noPermissions,
          options: {
            variant: SNACKBAR_VARIANTS.warning,
          },
        }),
      );
      updateRoute({
        path: ALL_ROUTES.root.path,
        params: { currentAccountUuid: _metadata || metadata?.currentAccount?.uuid },
      });
      return;
    }

    const path = generatePath(newAppRoute.path, newAppRoute.params);
    const search = !newAppRoute.removeQueryString &&
      qs.stringify({
        bc: btoa(JSON.stringify(newBc)),
      });

    await navigate(path, {
      search: search,
      replace: newAppRoute?.replace,
      state: {
        delay: options.addDelay ? QUERY_DELAY : 0,
      },
    });
  };

  const value = useMemo(
    () => ({
      // setAppRoute: debounce(setAppRoute, DEBOUNCE_DELAY),
      setAppRoute: updateRoute,
      breadcrumbs: bc,
    }),
    [bc],
  );

  return (
    <AppRouteContext.Provider value={value}>
      {children}
    </AppRouteContext.Provider>
  );
};

AppRouteProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default AppRouteProvider;
